import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Archive, MailOpen, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AdminCard from "./AdminCard";

const STATUS_BADGE = {
    new: { label: "Nowa", cls: "bg-amber-100 text-amber-800" },
    read: { label: "Przeczytana", cls: "bg-blue-100 text-blue-800" },
    archived: { label: "Archiwum", cls: "bg-gray-200 text-gray-700" },
};

export default function KontaktAdmin({ onUpdate }) {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("new");
    const load = () => api.get("/contact/messages").then((r) => setItems(r.data || []));
    useEffect(() => { load(); }, []);

    const setStatus = async (id, status) => {
        try {
            await api.patch(`/contact/messages/${id}?status=${status}`);
            toast.success(status === "archived" ? "Zarchiwizowano." : status === "read" ? "Oznaczono jako przeczytane." : "Zmieniono status.");
            await load();
            onUpdate?.();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };
    const del = async (id) => {
        if (!window.confirm("Trwale usunąć tę wiadomość?")) return;
        try {
            await api.delete(`/contact/messages/${id}`);
            toast.success("Usunięto.");
            await load();
            onUpdate?.();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };

    const counts = {
        new: items.filter((m) => (m.status || "new") === "new").length,
        read: items.filter((m) => m.status === "read").length,
        archived: items.filter((m) => m.status === "archived").length,
        all: items.length,
    };
    const filtered = filter === "all" ? items : items.filter((m) => (m.status || "new") === filter);

    return (
        <AdminCard>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
                <h3 className="font-serif text-xl text-[#092E22]">Wiadomości z formularza kontaktowego</h3>
                <div className="flex gap-1 bg-[#FAF8F3] rounded-full p-1" data-testid="admin-msg-filter">
                    {["new", "read", "archived", "all"].map((k) => (
                        <button
                            key={k}
                            onClick={() => setFilter(k)}
                            data-testid={`admin-msg-filter-${k}`}
                            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${filter === k ? "bg-[#0D4F3C] text-white" : "text-gray-600 hover:text-[#0D4F3C]"}`}
                        >
                            {k === "new" ? "Nowe" : k === "read" ? "Przeczytane" : k === "archived" ? "Archiwum" : "Wszystkie"} ({counts[k]})
                        </button>
                    ))}
                </div>
            </div>
            <div className="space-y-3 max-h-[700px] overflow-y-auto">
                {filtered.length === 0 && <p className="text-gray-500">Brak wiadomości w tym widoku.</p>}
                {filtered.map((m) => {
                    const status = m.status || "new";
                    const badge = STATUS_BADGE[status];
                    return (
                        <div key={m.id} className="p-5 rounded-2xl bg-[#FAF8F3] border border-gray-100" data-testid={`admin-msg-${m.id}`}>
                            <div className="flex justify-between items-start mb-2 gap-3 flex-wrap">
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <p className="font-semibold text-[#092E22]">{m.name}</p>
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold ${badge.cls}`}>{badge.label}</span>
                                    </div>
                                    <p className="text-xs text-gray-500">{m.email}{m.phone ? ` • ${m.phone}` : ""}</p>
                                </div>
                                <span className="text-xs text-gray-400">{new Date(m.created_at).toLocaleString("pl-PL")}</span>
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-line mb-3">{m.message}</p>
                            {m.email_sent && (
                                <p className="mb-3 text-[11px] text-emerald-700">✓ Powiadomienie e-mail wysłane</p>
                            )}
                            {m.email_result?.failed > 0 && m.email_result?.errors?.length > 0 && (
                                <div className="mb-3 p-3 rounded-xl bg-red-50 border border-red-200 text-xs" data-testid={`admin-msg-email-errors-${m.id}`}>
                                    <p className="font-semibold text-red-900 mb-1">✉️ Powiadomienie e-mail nie zostało wysłane do {m.email_result.failed} odbiorcy(ów). Odpowiedź dostawcy e-mail:</p>
                                    <ul className="list-disc pl-5 space-y-0.5 text-red-800 break-words font-mono">
                                        {m.email_result.errors.map((e, i) => <li key={i}>{e}</li>)}
                                    </ul>
                                </div>
                            )}
                            {m.email_error && (
                                <div className="mb-3 p-3 rounded-xl bg-red-50 border border-red-200 text-xs text-red-800" data-testid={`admin-msg-email-error-${m.id}`}>
                                    <p className="font-semibold mb-1">✉️ Wyjątek podczas wysyłki:</p>
                                    <code className="font-mono break-words">{m.email_error}</code>
                                </div>
                            )}
                            <div className="flex items-center gap-2">
                                {status === "new" && (
                                    <button onClick={() => setStatus(m.id, "read")} data-testid={`admin-msg-read-${m.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-blue-200 text-blue-700 text-xs hover:bg-blue-50">
                                        <MailOpen size={12} /> Oznacz jako przeczytane
                                    </button>
                                )}
                                {status !== "archived" && (
                                    <button onClick={() => setStatus(m.id, "archived")} data-testid={`admin-msg-archive-${m.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-gray-200 text-gray-700 text-xs hover:bg-gray-50">
                                        <Archive size={12} /> Archiwizuj
                                    </button>
                                )}
                                {status === "archived" && (
                                    <button onClick={() => setStatus(m.id, "read")} data-testid={`admin-msg-restore-${m.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-gray-200 text-gray-700 text-xs hover:bg-gray-50">
                                        Przywróć
                                    </button>
                                )}
                                <button onClick={() => del(m.id)} data-testid={`admin-msg-delete-${m.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-red-200 text-red-700 text-xs hover:bg-red-50">
                                    <Trash2 size={12} /> Usuń
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </AdminCard>
    );
}
